import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, NavLink } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  LayoutDashboard, 
  ShoppingBag,
  Tag,
  TrendingUp,
  Coins,
  LogOut,
  Plus,
  Trash2,
  ExternalLink,
  Loader2,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

// Dashboard Layout Component
function DashboardLayout({ children, auth }) {
  const navigate = useNavigate();
  const { user, logout } = auth;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { to: '/ecommerce', icon: LayoutDashboard, label: 'Vue d\'ensemble', end: true },
    { to: '/ecommerce/promotions', icon: Tag, label: 'Promotions' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-stone-200 flex flex-col fixed h-full">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                InvesThune
              </span>
              <p className="text-xs text-slate-600 font-medium">E-commerce</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span className="text-slate-700 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.company_name || user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
}

// Overview Page
function OverviewPage({ auth }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, user } = auth;

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const response = await fetch(`${API}/promotions`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          setPromotions(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch promotions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPromotions();
  }, [token]);

  const activePromotions = promotions.filter(p => p.is_active).length;
  const totalOrders = promotions.reduce((sum, p) => sum + p.orders_count, 0);
  const avgDiscount = promotions.length > 0 
    ? promotions.reduce((sum, p) => sum + p.discount_percentage, 0) / promotions.length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="ecommerce-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Bienvenue, {user?.company_name || user?.name}
        </h1>
        <p className="text-slate-600">Tableau de bord de votre e-commerce</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Promotions actives</p>
                <p className="text-3xl font-bold text-slate-800 counter">{activePromotions}</p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Tag className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Commandes générées</p>
                <p className="text-3xl font-bold text-emerald-700 counter">{totalOrders}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Réduction moyenne</p>
                <p className="text-3xl font-bold text-orange-600 counter">{avgDiscount.toFixed(0)}%</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Percent className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Promotions */}
      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Vos promotions
          </CardTitle>
          <NavLink to="/ecommerce/promotions">
            <Button variant="outline" size="sm" className="rounded-full">
              Voir tout
            </Button>
          </NavLink>
        </CardHeader>
        <CardContent>
          {promotions.length === 0 ? (
            <div className="text-center py-8">
              <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune promotion créée</p>
              <NavLink to="/ecommerce/promotions">
                <Button className="mt-4 bg-slate-800 hover:bg-slate-900 rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une promotion
                </Button>
              </NavLink>
            </div>
          ) : (
            <div className="space-y-4">
              {promotions.slice(0, 3).map((promo) => (
                <div key={promo.promotion_id} className="p-4 bg-stone-50 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-800">{promo.title}</h4>
                      <p className="text-sm text-slate-500">{promo.description.substring(0, 60)}...</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                        -{promo.discount_percentage}%
                      </span>
                      <p className="text-sm text-slate-500 mt-1">{promo.orders_count} commandes</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Partner Image */}
      <div className="mt-8 rounded-2xl overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1770013413878-2530e2c3d82b?w=1200&h=400&fit=crop" 
          alt="Partenaire e-commerce"
          className="w-full h-64 object-cover"
        />
      </div>
    </div>
  );
}

// Promotions Page
function PromotionsPage({ auth }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_percentage: '',
    banner_url: '',
    product_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { token } = auth;

  const fetchPromotions = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API}/promotions`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        setPromotions(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/promotions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          discount_percentage: parseFloat(formData.discount_percentage)
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la création');

      toast.success('Promotion créée avec succès');
      setIsOpen(false);
      setFormData({ title: '', description: '', discount_percentage: '', banner_url: '', product_url: '' });
      fetchPromotions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (promotionId) => {
    if (!confirm('Désactiver cette promotion ?')) return;

    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/promotions/${promotionId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      toast.success('Promotion désactivée');
      fetchPromotions();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div data-testid="promotions-manage-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestion des promotions
          </h1>
          <p className="text-slate-600">Créez et gérez vos offres promotionnelles</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-800 hover:bg-slate-900 rounded-full" data-testid="add-promotion-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle promotion
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle promotion</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre de la promotion</Label>
                <Input
                  placeholder="Soldes d'été -30%"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="promo-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Décrivez votre promotion..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="promo-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Réduction (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  placeholder="30"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                  required
                  data-testid="promo-discount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL de la bannière (optionnel)</Label>
                <Input
                  placeholder="https://..."
                  value={formData.banner_url}
                  onChange={(e) => setFormData({ ...formData, banner_url: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>URL du produit (optionnel)</Label>
                <Input
                  placeholder="https://votre-site.com/produit"
                  value={formData.product_url}
                  onChange={(e) => setFormData({ ...formData, product_url: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-slate-800 hover:bg-slate-900"
                  disabled={submitting}
                  data-testid="submit-promo-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : promotions.length === 0 ? (
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardContent className="text-center py-12">
            <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune promotion créée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {promotions.map((promo) => (
            <Card key={promo.promotion_id} className="bg-white border-stone-200 rounded-2xl overflow-hidden">
              {promo.banner_url && (
                <img 
                  src={promo.banner_url} 
                  alt={promo.title}
                  className="w-full h-40 object-cover"
                />
              )}
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {promo.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                        -{promo.discount_percentage}%
                      </span>
                      <span className={`badge ${promo.is_active ? 'badge-success' : 'badge-warning'}`}>
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(promo.promotion_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <p className="text-slate-600 text-sm mb-4">{promo.description}</p>
                
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    {promo.orders_count} commandes
                  </p>
                  {promo.product_url && (
                    <a 
                      href={promo.product_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800"
                    >
                      Voir le produit
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>

                <p className="text-xs text-slate-400 mt-4">
                  Créée le {new Date(promo.created_at).toLocaleDateString('fr-FR')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Main E-commerce Dashboard Component
export default function EcommerceDashboard({ auth }) {
  return (
    <DashboardLayout auth={auth}>
      <Routes>
        <Route index element={<OverviewPage auth={auth} />} />
        <Route path="promotions" element={<PromotionsPage auth={auth} />} />
      </Routes>
    </DashboardLayout>
  );
}
